import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faClose,
} from '@fortawesome/free-solid-svg-icons';
import { createTimezoneIndependentDate } from '../../../utils/dateUtils';
import Loader from '../../../components/Common/Loader';
import BackButton from '../../../components/Common/BackButton';

export interface SelectedDateTime {
  startDate?: Date;
  startDateTime?: string;
}

export type DateSelectorMode = 'date' | 'month';

export interface NewDatePickerPopupProps {
  isOpen: boolean;
  onClose?: (mode: DateSelectorMode) => void;
  onDateSelected: (date: SelectedDateTime, mode: DateSelectorMode) => void;
  onContinue?: (date: SelectedDateTime, mode: DateSelectorMode) => void;
  selectedDate?: Date;
  isLoading?: boolean;
  isMonthSelection?: boolean;
  showTopRightCloseButton?: boolean;
}

type DatePickerMode = 'initial' | 'pick-date' | 'select-month';

const NewDatePickerPopup: React.FC<NewDatePickerPopupProps> = ({
  isOpen,
  onClose,
  onDateSelected,
  onContinue,
  selectedDate,
  isLoading = false,
  isMonthSelection = false,
  showTopRightCloseButton = false,
}) => {
  const [mode, setMode] = useState<DatePickerMode>(isMonthSelection ? 'initial' : 'pick-date');
  const [currentDate, setCurrentDate] = useState<Date>(selectedDate || new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(selectedDate || new Date());
  const [selectedMonth, setSelectedMonth] = useState<number>(selectedDate?.getMonth() || new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(selectedDate?.getFullYear() || new Date().getFullYear());
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const [selectedDateDay, setSelectedDateDay] = useState<number>(selectedDate?.getDate() || new Date().getDate());

  useEffect(() => {
    if (isMonthSelection) {
      setMode('initial');
    } else {
      setMode('pick-date');
    }
  }, [isMonthSelection]);

  useEffect(() => {
    return () => {
      setMode('pick-date');
      setIsInfoExpanded(false);
      setCurrentDate(selectedDate || new Date());
      setCurrentMonth(selectedDate || new Date());
      setSelectedMonth(selectedDate?.getMonth() || new Date().getMonth());
      setSelectedYear(selectedDate?.getFullYear() || new Date().getFullYear());
      setSelectedDateDay(selectedDate?.getDate() || new Date().getDate());
    };
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setCurrentDate(selectedDate);
      setCurrentMonth(selectedDate);
      setSelectedMonth(selectedDate.getMonth());
      setSelectedYear(selectedDate.getFullYear());
      setSelectedDateDay(selectedDate.getDate());
    }
  }, [selectedDate]);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);

  const getAvailableMonthIndices = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    if (selectedYear === currentYear) {
      return Array.from({ length: 12 - currentMonth - 1 }, (_, i) => currentMonth + 1 + i);
    }
    return Array.from({ length: 12 }, (_, i) => i);
  };

  // Calendar helper functions
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const prevMonthLastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDate();

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isDateSelected = (day: number) => {
    if (!currentDate?.getTime()) return false;
    return (
      day === selectedDateDay &&
      currentMonth.getMonth() === currentDate.getMonth() &&
      currentMonth.getFullYear() === currentDate.getFullYear()
    );
  };

  const isDateDisabled = (day: number) => {
    const dateToCheck = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck < today;
  };

  const handleCalendarDateSelect = (day: number) => {
    if (isDateDisabled(day)) return;
    const newDate = createTimezoneIndependentDate(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setCurrentDate(newDate);
    setSelectedDateDay(day);
  };

  // Generate calendar days
  const calendarDays: any[] = [];

  // Add previous month's days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthLastDay - i,
      isPreviousMonth: true,
    });
  }

  // Add current month's days
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: true,
    });
  }

  // Add next month's days to fill the grid
  const remainingDays = 42 - calendarDays.length;
  for (let i = 1; i <= remainingDays; i++) {
    calendarDays.push({
      day: i,
      isNextMonth: true,
    });
  }

  const handleContinue = (mode: DateSelectorMode) => {
    const selectedDateTime: SelectedDateTime = {
      startDate: currentDate,
      startDateTime: '10:00 AM',
    };

    onDateSelected(selectedDateTime, mode);
    if (onContinue) {
      onContinue(selectedDateTime, mode);
    }
  };

  const handleMonthSelection = () => {
    const newDate = createTimezoneIndependentDate(selectedYear, selectedMonth, 1);
    setCurrentDate(newDate);
    setCurrentMonth(newDate);
    setSelectedDateDay(1);

    const selectedDateTime: SelectedDateTime = {
      startDate: newDate,
      startDateTime: '10:00 AM',
    };
    onDateSelected(selectedDateTime, 'month');
    // setMode('pick-date');
    if (onContinue) {
      onContinue(selectedDateTime, 'month');
    }
  };

  const renderInitialView = () => (
    <div className="p-8 text-center font-sofiaSans">
      <h2 className="text-xl md:text-2xl font-bold text-black mb-6">When will you start?</h2>

      <div className="space-y-4 p-4 mb-6 border border-secondaryStoryWhiteGreyBorderColor rounded-xl">
        <div className="flex items-center justify-center mb-4">
          <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-[#1a1a1a]" />
          <span className="font-bold text-secondaryStoryBlackText">Pick a date</span>
        </div>
        <p className="text-sm text-left text-secondaryStoryBlackText mb-4">
          Choose the exact day your class begins. Don’t worry, you can change this anytime.
        </p>
        <div className="flex items-center justify-center">
          <button
            onClick={() => setMode('pick-date')}
            className={`px-8 sm:px-10 lg:px-6 py-2 h-9 border border-secondaryStoryAdditionalLightBlueText bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-sm`}
          >
            Select Date
          </button>
        </div>
      </div>

      <div className="relative px-4">
        <p className="text-sm text-secondaryStoryDescriptionGreyTextColor mb-4 text-left">
          Not sure on the day? Choose a month. We'll remind you to set a date before you start.
        </p>
        <div className="flex items-center justify-center">
          <button
            onClick={() => setMode('select-month')}
            className={`px-8 sm:px-10 lg:px-6 py-2 h-9 border border-secondaryBorderBackgroundLightGreyColor bg-transparent text-secondaryStoryOptionalLightBlueText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-sm`}
          >
            Select Month
          </button>
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={() => setIsInfoExpanded(!isInfoExpanded)}
          className="flex items-center justify-center w-full text-sm text-secondaryStoryBlackText hover:text-gray-800 transition-colors"
        >
          <span>What happens around your start date?</span>
          <FontAwesomeIcon icon={isInfoExpanded ? faChevronUp : faChevronDown} className="ml-2 text-xs" />
        </button>

        {isInfoExpanded && (
          <div className="mt-4 text-left font-sofiaSans space-y-3 text-xs text-secondaryStoryDescriptionMiddleGreyTextColor">
            <div>
              <p className="font-bold mb-2">Before your start date</p>
              <ul className="space-y-1 ml-1">
                <li className="flex items-start">
                  <div className="flex items-center py-1">
                    <span className="w-1 h-1 bg-secondaryStoryDescriptionMiddleGreyTextColor rounded-full mr-2 flex-shrink-0"></span>
                  </div>
                  💻 Explore your dashboard for guides and writing sheets.
                </li>
                <li className="flex items-start">
                  <div className="flex items-center py-1">
                    <span className="w-1 h-1 bg-secondaryStoryDescriptionMiddleGreyTextColor rounded-full mr-2 flex-shrink-0"></span>
                  </div>
                  <div>
                    📨 Please send home flyers <span className="font-bold">7 days before start</span>. Digital responses
                    are shown on your dashboard. Students <span className="font-bold">can start</span> even if some
                    don’t reply.
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <p className="font-bold mb-2">On your start date</p>
              <ul className="space-y-1 ml-1">
                <li className="flex items-center">
                  <div className="flex items-center py-1">
                    <span className="w-1 h-1 bg-secondaryStoryDescriptionMiddleGreyTextColor rounded-full mr-2 flex-shrink-0"></span>
                  </div>
                  📖 Print the handouts and play the intro video.
                </li>
                <li className="flex items-center">
                  <div className="flex items-center py-1">
                    <span className="w-1 h-1 bg-secondaryStoryDescriptionMiddleGreyTextColor rounded-full mr-2 flex-shrink-0"></span>
                  </div>
                  ✍️ Students begin their stories with the writing sheets.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDatePicker = () => (
    <div className="py-6 px-11 font-sofiaSans">
      <h2 className="text-lg font-bold text-black mb-4 text-center">Choose Start Date</h2>

      <p className="text-sm text-left text-secondaryStoryBlackText mb-6">
        <span className="font-bold">Publishing Requirement</span> - Keep your start date updated to ensure student
        access and on-time delivery. Celebration Day is roughly 28 days later, so choose a date at least 28 days before
        school ends or holidays.
      </p>

      {/* Calendar */}
      <div className="mb-6 px-6 sm:px-12">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPrevMonth}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Previous month"
            disabled={
              currentMonth.getFullYear() === new Date().getFullYear() &&
              currentMonth.getMonth() === new Date().getMonth()
            }
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4 text-secondaryStoryBlackText" />
          </button>
          <span className="font-medium text-secondaryStoryBlackText">{formatMonthYear(currentMonth)}</span>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Next month"
          >
            <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-secondaryStoryBlackText" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-secondaryStoryBlackText py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((item, index) => {
            const isDisabled = item.isCurrentMonth && isDateDisabled(item.day);
            return (
              <button
                key={index}
                onClick={() => item.isCurrentMonth && !isDisabled && handleCalendarDateSelect(item.day)}
                className={`
                  h-8 w-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors
                  ${item.isCurrentMonth && !isDisabled ? 'hover:bg-gray-100 text-secondaryStoryBlackText' : 'text-gray-300 cursor-default'}
                  ${isDisabled ? 'text-gray-300 cursor-not-allowed opacity-50' : ''}
                  ${isDateSelected(item.day) && item.isCurrentMonth && !isDisabled ? 'bg-secondaryStoryAdditionalLightBlueText text-white hover:bg-secondaryStoryAdditionalLightBlueText' : ''}
                `}
                disabled={!item.isCurrentMonth || isDisabled}
              >
                {item.day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderMonthPicker = () => (
    <div className="p-11 font-sofiaSans">
      <h2 className="text-lg font-bold text-black mb-4 text-center">Choose a Target Month</h2>

      <p className="text-sm text-left text-secondaryStoryBlackText mb-6">
        Pick a month to start. You’ll set the exact date later. Celebration is 28 days after your start date, so avoid
        holidays and the last day of school.
      </p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm text-left font-bold text-secondaryStoryBlackText mb-2">Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-secondaryStoryWhiteGreyBorderColor rounded-lg focus:ring-1 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText outline-none appearance-none bg-white text-secondaryStoryBlackText font-medium text-sm cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.75rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.25rem 1.25rem',
              paddingRight: '2.5rem',
            }}
          >
            {getAvailableMonthIndices().map((monthIndex) => (
              <option key={monthIndex} value={monthIndex}>
                {months[monthIndex]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-left font-bold text-secondaryStoryBlackText mb-2">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-secondaryStoryWhiteGreyBorderColor rounded-lg focus:ring-1 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText outline-none appearance-none bg-white text-secondaryStoryBlackText font-medium text-sm cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.75rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.25rem 1.25rem',
              paddingRight: '2.5rem',
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-sm text-secondaryStoryDescriptionGreyTextColor text-left">
        We'll remind you to set your exact start date before your month begins.
      </p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
        {showTopRightCloseButton && (
          <FontAwesomeIcon
            icon={faClose}
            className="text-xl text-storyblue p-2 mr-4 sm:mr-5.5 lg:mr-7 mt-3.5 z-10"
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              cursor: 'pointer',
            }}
            onClick={() => onClose && onClose('date')}
          />
        )}

        {mode === 'initial' && renderInitialView()}
        {mode === 'pick-date' && renderDatePicker()}
        {mode === 'select-month' && renderMonthPicker()}

        {/* Footer */}
        <div className="flex items-center justify-between px-8 pb-4 pt-0 rounded-b-2xl">
          <div>
            {!showTopRightCloseButton && (onClose || mode !== 'initial') && (
              <BackButton
                onClick={
                  mode === 'initial' || !isMonthSelection ? () => onClose && onClose('date') : () => setMode('initial')
                }
              ></BackButton>
            )}
          </div>

          {(mode === 'pick-date' || mode === 'select-month') && (
            <button
              onClick={mode === 'pick-date' ? () => handleContinue('date') : () => handleMonthSelection()}
              disabled={isLoading}
              className={`px-8 sm:px-10 lg:px-6 py-2 h-9 border border-secondaryStoryAdditionalLightBlueText bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-sm`}
            >
              Continue
              {isLoading && <Loader loader={isLoading} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewDatePickerPopup;
